import { InfoList } from '@orobix/orobix-fe-components'
import { FormikErrors } from 'formik'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { DatasetSelectField } from '../../components/DatasetSelect'
import ObservationsSelectField from '../../components/ObservationsSelectField/ObservationsSelectField'
import { USER_LEVEL } from '../../components/Settings/definitions'
import { SettingsContext } from '../../components/Settings/SettingsContext'
import { useStore } from '../../store/store'
import { createTraining, Metadata } from '../../store/storeActions'
import { BackboneSelectOption } from '../../types/backbone'
import {
  countEmptyAnnotationObservations,
  countNotAnnotatedObservations,
  countValidAnnotationObservations,
} from '../../utils/forms'
import BackboneSelect from './BackboneSelect/BackboneSelect'
import { MAX_TRAINING_IMAGES } from './const'
import FP16Switch from './FP16Switch'
import ResolutionInputRange from './ResolutionSelect/ResolutionInputRange'
import ResolutionSelect from './ResolutionSelect/ResolutionSelect'
import useStyles from './ResolutionSelect/ResolutionSelect.styles'
import TrainingNewForm, {
  SelectOption,
  TrainingNewFormValues,
} from './TrainingNewForm'
import {
  getEstimatedTrainingsDuration,
  getResolutionsErrorMessage,
  ITrainingDuration,
} from './utils'

export type SegmentationFormValues = TrainingNewFormValues

interface RouteParams {
  projectId: string
  productId: string
}

const requiredImages = 5

const NewSegmentationTraining = () => {
  const { t } = useTranslation()
  const { product } = useStore()
  const history = useHistory()
  const { settings } = useContext(SettingsContext)
  const { productId, projectId } = useParams<RouteParams>()
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])
  const [selectedBackbone, setSelectedBackbone] = useState<
    BackboneSelectOption[] | undefined
  >()
  const resolutionDftValue = { value: 'standard', label: 'Standard' }
  const [selectedResolution, setSelectedResolution] = useState<
    SelectOption | undefined
  >(resolutionDftValue)
  const MAX_RESOLUTION = 500000
  const DFT_RES = 224
  const STEP = 32
  const [customHeight, setCustomHeight] = useState(DFT_RES)
  const [customWidth, setCustomWidth] = useState(DFT_RES)
  const [estimatedTrainingsDuration, setEstimatedTrainingsDuration] = useState<
    ITrainingDuration[]
  >([])
  const [
    hasNonAnnotatedImagesInDataset,
    setHasNonAnnotatedImagesInDataset,
  ] = useState(false)

  const styles = useStyles()

  function showHighResolutionFlag(backboneValues: BackboneSelectOption[]) {
    if (!backboneValues || backboneValues.length === 0) {
      return false
    }
    return backboneValues.every(
      (b) => b.metadata?.trainingResolutionSupported !== false
    )
  }

  return product ? (
    <TrainingNewForm<SegmentationFormValues>
      productType={product.type}
      productId={productId}
      requiredImages={requiredImages}
      initialValues={{
        selectedObservations: [],
        name: '',
        notes: '',
        startTest: false,
        fp16: true,
      }}
      renderRightColumn={({ values, setFieldValue }) => (
        <>
          <InfoList
            list={[
              {
                title: t('Not annotated'),
                value: countNotAnnotatedObservations(
                  values.selectedObservations
                ),
              },
              {
                title: t('Empty'),
                value: countEmptyAnnotationObservations(
                  values.selectedObservations
                ),
              },
              {
                title: t('Annotated'),
                value: countValidAnnotationObservations(
                  values.selectedObservations
                ),
              },
            ].filter((item) => item.value > 0)}
          />
          <InfoList
            list={[
              {
                title: t('Total images'),
                value: values.selectedObservations.length,
              },
            ]}
          />

          {settings.preference__user_level === USER_LEVEL.expert && (
            <DatasetSelectField
              fieldName="dataset"
              productId={Number(productId)}
              onSelectedDatasets={setSelectedDatasets}
            />
          )}

          <BackboneSelect
            fieldName="backbone"
            productId={product.id}
            onSelectedBackbone={setSelectedBackbone}
            onSelectedResolution={setSelectedResolution}
          />

          <FP16Switch fieldName="fp16" />

          <ResolutionSelect
            fieldName="resolution"
            productId={product.id}
            disabled={
              values.backbone === undefined ||
              !showHighResolutionFlag(values.backbone) ||
              settings.preference__user_level === USER_LEVEL.basic
            }
            dftValue={resolutionDftValue}
            onSelectedResolution={setSelectedResolution}
            setCustomHeight={setCustomHeight}
            setCustomWidth={setCustomWidth}
            backboneValues={selectedBackbone}
            producType={product.type}
          />
          <div
            className={
              selectedResolution?.value === 'custom'
                ? styles.visible
                : styles.hidden
            }
          >
            <ResolutionInputRange
              customHeight={customHeight}
              customWidth={customWidth}
              setCustomHeight={setCustomHeight}
              setCustomWidth={setCustomWidth}
              getResolutionsErrorMessage={getResolutionsErrorMessage}
              maxResolution={MAX_RESOLUTION}
              step={STEP}
              productType={product.type}
              t={t}
              onResolutionInputRangeChange={(newHeight, newWidth) => {
                setEstimatedTrainingsDuration(
                  getEstimatedTrainingsDuration({
                    productType: product.type,
                    numOfImages: values.selectedObservations.length,
                    backbones: values.backbone?.map((b) => ({
                      code: b.code,
                      label: b.label,
                    })),
                    resolution: {
                      height: newHeight,
                      width: newWidth,
                    },
                    precision: values.fp16 ? 'fp16' : 'fp32',
                  })
                )
              }}
            />
          </div>
        </>
      )}
      validate={(values) => {
        const errors: FormikErrors<{ [field: string]: string }> = {}

        const resolutionsError = getResolutionsErrorMessage(
          customHeight,
          customWidth,
          MAX_RESOLUTION,
          product.type,
          t
        )

        const len = countValidAnnotationObservations(
          values.selectedObservations
        )
        if (len < requiredImages) {
          errors.selectedObservations = t(
            'You need to use at least {{ count }} images with a valid annotation',
            { count: requiredImages }
          )
        } else if (len > MAX_TRAINING_IMAGES) {
          errors.selectedObservations = t(
            'To avoid long training times, you can select up to {{ count }} images',
            { count: MAX_TRAINING_IMAGES }
          )
        }

        if (resolutionsError !== '') {
          errors.selectedObservations = resolutionsError
        }

        const notAnnotatedObservations = countNotAnnotatedObservations(
          values.selectedObservations
        )

        if (notAnnotatedObservations > 0) {
          errors.selectedObservations = t(
            'You added {{ count }} image without annotation. Please remove or annotate it.',
            { count: notAnnotatedObservations }
          )
        }

        if (!values.backbone || values.backbone.length === 0) {
          errors.backbone = t('This field is required')
        }

        if (!values.name) {
          errors.name = t('This field is required')
        }

        if (
          values.startTest &&
          values.selectedObservations.length === product.observationsNumber
        ) {
          errors.startTest = t(
            'Deselect at least one image if you want to start a test with unselected images. Otherwise, deselect this option.'
          )
        }

        const trainingDurations = getEstimatedTrainingsDuration({
          productType: product.type,
          numOfImages: values.selectedObservations.length,
          backbones: values.backbone?.map((b) => ({
            code: b.code,
            label: b.label,
          })),
          resolution: {
            height: customHeight,
            width: customWidth,
          },
          precision: values.fp16 ? 'fp16' : 'fp32',
        })

        if (Object.keys(errors).length === 0) {
          setEstimatedTrainingsDuration(trainingDurations)
        }

        if (
          trainingDurations.find((d) => d.sec > 480 * 60) // 8 hours
        ) {
          errors.selectedObservations = t(
            'The estimated training time is too long. Please select fewer images or lower the resolution (if possible).'
          )
        }

        return errors
      }}
      estimatedTrainingsDuration={estimatedTrainingsDuration}
      hasNonAnnotatedImagesInDataset={hasNonAnnotatedImagesInDataset}
      onSubmit={(values, setSubmitting) => {
        if (values.backbone !== undefined) {
          const backbones: BackboneSelectOption[] = (values.backbone as unknown) as BackboneSelectOption[]
          backbones.forEach((b: BackboneSelectOption) => {
            let metadata: Metadata | undefined
            if (values.fp16) metadata = { ...metadata, precision: 'fp16' }
            if (
              values.resolution?.code !== 'Standard' &&
              b.metadata.trainingResolutionSupported !== false
            ) {
              metadata = {
                ...metadata,
                inputHeight: customHeight,
                inputWidth: customWidth,
              }
            }
            createTraining(
              Number(projectId),
              Number(productId),
              Number(b.id),
              values.selectedObservations.map((o) => o.id),
              setSubmitting,
              history.push,
              metadata,
              values.name,
              values.notes,
              values.startTest,
              selectedDatasets.length > 0
                ? selectedDatasets.map((id) => Number(id))
                : undefined
            )
          })
        }
      }}
    >
      {/* overrides the original ObservationSelectField to handle dataset */}
      <ObservationsSelectField
        name="selectedObservations"
        productType={product.type}
        productId={Number(productId)}
        dataset={selectedDatasets.length > 0 ? selectedDatasets : undefined}
        inTraining
        inTest={false}
        onNonAnnotatedImagesInDataset={setHasNonAnnotatedImagesInDataset}
      />
    </TrainingNewForm>
  ) : null
}

export default NewSegmentationTraining
